<script setup lang="ts">
/** First Directors Resolution — SG Incorporation (Post) */
import { ref, inject, watch, type Ref } from 'vue';
import CddVarField from '../incorporation-pre/cdd/CddVarField.vue';
import DocScope from '../incorporation-pre/cdd/DocScope.vue';
import SgPostSigners from './SgPostSigners.vue';
import { SG_POST_SIGNER_KEYS } from './sgPostSignersContext';
import './allotDoc.css';
import type { SgPostChildLocal } from './sgPostChildLocal';

let dSeq = 1;
let aSeq = 1;
const directors = ref<number[]>([1]);
const allottees = ref<number[]>([1]);
function addDir() { directors.value.push(++dSeq); }
function delDir(id: number) { if (directors.value.length > 1) directors.value = directors.value.filter((x) => x !== id); }
function addAllot() { allottees.value.push(++aSeq); }
function delAllot(id: number) { if (allottees.value.length > 1) allottees.value = allottees.value.filter((x) => x !== id); }

const childLocal = inject<Ref<SgPostChildLocal> | null>('sgIncPostChildLocal', null);
watch(
  () => childLocal?.value,
  (v) => {
    if (!v) return;
    if (Array.isArray(v.firstResDirectorIds) && v.firstResDirectorIds.length) {
      directors.value = [...v.firstResDirectorIds];
      dSeq = Math.max(dSeq, ...directors.value);
    }
    if (Array.isArray(v.firstResAllotteeIds) && v.firstResAllotteeIds.length) {
      allottees.value = [...v.firstResAllotteeIds];
      aSeq = Math.max(aSeq, ...allottees.value);
    }
  },
  { deep: true, immediate: true },
);
</script>

<template>
  <div class="allot-doc">
    <div class="doc-head">
      <div class="doc-company"><CddVarField field-key="company_name" label="Company Name" /></div>
      <div class="doc-meta">
        (Company Registration No.: <CddVarField field-key="company_registration_no" label="UEN / Registration No." />)<br />
        (Incorporated in Singapore)<br />
        (the "Company")
      </div>
      <div class="doc-lbl-title" style="margin-top:14px">DIRECTORS' RESOLUTIONS IN WRITING</div>
      <div class="doc-meta">PASSED PURSUANT TO THE COMPANY'S CONSTITUTION</div>
    </div>

    <p class="doc-lbl-title">INCORPORATION</p>
    <p class="doc-p">
      RESOLVED that it be noted that the Company was incorporated on
      <CddVarField field-key="date_of_incorporation" label="Date of Incorporation" />
      and that the Registration Number assigned to it is
      <CddVarField field-key="company_registration_no" label="Registration No." />.
      The Certificate of Incorporation was tabled together with a copy of the Constitution.
    </p>

    <p class="doc-lbl-title">FIRST DIRECTORS</p>
    <p class="doc-p">
      RESOLVED that it be noted and confirmed that the following persons, who were appointed as First
      Directors, have accepted office and, having consented to act, be and are hereby appointed Directors
      of the Company with effect from the date of incorporation:
    </p>
    <ul class="sg-bullets">
      <li v-for="id in directors" :key="id" class="row-li">
        <DocScope :scope="`dir_${id}/`">
          <CddVarField field-key="d_name" label="Director Name" />
        </DocScope>
        <button type="button" class="cdd-rowbtn" :disabled="directors.length <= 1" @click="delDir(id)">×</button>
      </li>
    </ul>
    <button type="button" class="cdd-addrow" @click="addDir">+ Add director</button>

    <p class="doc-lbl-title">FIRST SECRETARY</p>
    <p class="doc-p">
      RESOLVED that it be noted and confirmed that
      <CddVarField field-key="secretary_name" label="Secretary Name" />,
      who was appointed as First Secretary, has accepted office with effect from the date of incorporation.
    </p>

    <p class="doc-lbl-title">REGISTERED OFFICE</p>
    <p class="doc-p">
      RESOLVED that the registered office of the Company be situated at
      <CddVarField field-key="registered_office_address" label="Registered Office Address" />
      with effect from the date of incorporation.
    </p>

    <p class="doc-lbl-title">ALLOTMENT AND ISSUE OF SHARES</p>
    <p class="doc-p">
      RESOLVED that the signatories to the Constitution be registered as members in respect of the shares
      which are subscribed in cash, namely:
    </p>
    <table class="cdd-tbl">
      <thead>
        <tr>
          <th>Name of Allottee</th>
          <th>Number of Ordinary Shares Allotted</th>
          <th>Share Certificate Number</th>
          <th style="width:36px" />
        </tr>
      </thead>
      <tbody>
        <tr v-for="id in allottees" :key="id">
          <DocScope :scope="`allot_${id}/`">
            <td><CddVarField field-key="a_name" label="Allottee Name" /></td>
            <td><CddVarField field-key="a_number_of_shares" label="No. of Shares" /></td>
            <td><CddVarField field-key="a_share_certificate_number" label="Share Certificate No." /></td>
          </DocScope>
          <td><button type="button" class="cdd-rowbtn" :disabled="allottees.length <= 1" @click="delAllot(id)">×</button></td>
        </tr>
        <tr><td colspan="4"><button type="button" class="cdd-addrow" @click="addAllot">+ Add allottee</button></td></tr>
      </tbody>
    </table>
    <p class="doc-p" style="margin-top:8px">
      Share certificates be issued to the above allottees in accordance with the provisions of the Company's Constitution.
    </p>

    <p class="doc-lbl-title">COMMON SEAL</p>
    <p class="doc-p">
      RESOLVED that pursuant to Section 41A of the Companies Act 1967, the Company has elected not to have a common seal.
    </p>

    <p class="doc-lbl-title">FINANCIAL YEAR</p>
    <p class="doc-p">
      RESOLVED that the first financial period of the Company shall commence on
      <CddVarField field-key="date_of_incorporation" label="Date of Incorporation" />
      (the date of incorporation) and end on
      <CddVarField field-key="first_financial_year_end" label="First FYE" />,
      and thereafter, the financial year of the Company shall end on
      <CddVarField field-key="subsequent_financial_year_end" label="Subsequent FYE" />
      of each year until otherwise decided.
    </p>

    <p class="doc-p" style="margin-top:16px">
      Date: <CddVarField field-key="resolution_date" label="Resolution Date" />
    </p>
    <SgPostSigners :var-key="SG_POST_SIGNER_KEYS.firstRes" board-label="BOARD OF DIRECTORS" />
  </div>
</template>

<style scoped>
.sg-bullets { margin: 0 0 8px; padding-left: 22px; font-size: 12.5px; line-height: 1.55; }
.row-li { display: flex; align-items: baseline; gap: 8px; margin-bottom: 4px; }
</style>
